import { useRef, useState, useEffect } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { createPortal } from "react-dom";
import type { ComponentPropsWithoutRef } from "react";
import { SourceType } from "../Chat/Source";
import SourceFooter from "../Chat/SourceFooter";
import { ShellLink } from "../ShellNavigation/ShellAwareLink";

// 定义 Quotes 组件的 props 类型
type QuotesProps = ComponentPropsWithoutRef<"span"> & {
  node?: any;
  children?: React.ReactNode;
  source?: SourceType[];
};

const Quotes = ({ node, children, ...props }: QuotesProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const sourceIndex = typeof children === "string" ? children?.split(",") : "";
  const [isHovering, setIsHovering] = useState(false);
  const [isBrowser, setIsBrowser] = useState(false);
  const [portal, setPortal] = useState<React.ReactPortal | null>(null);
  const [isContentReady, setIsContentReady] = useState(false);

  // 确保只在客户端渲染Portal
  useEffect(() => {
    setIsBrowser(true);
  }, []);

  const rect = ref.current?.getBoundingClientRect?.();

  const quotesSource =
    props?.source?.filter((_, index) => {
      return sourceIndex.includes(`${index + 1}`);
    }) || [];

  const hasSourceContent = quotesSource?.length > 0;

  // 判断是否有内容可以渲染
  const canRenderPopup = hasSourceContent && !!rect && isBrowser;

  // 处理悬停效果和Portal创建
  useEffect(() => {
    if (!canRenderPopup || !isHovering) {
      setPortal(null);
      setIsContentReady(false);
      return;
    }

    // 异步预先准备内容，避免阻塞主线程
    const prepareContent = async () => {
      // 先设置标记为未准备好
      setIsContentReady(false);

      // 使用Promise模拟异步内容准备
      const contentElements = await new Promise<React.ReactNode[]>(
        (resolve) => {
          // 使用requestAnimationFrame确保在浏览器绘制前完成
          requestAnimationFrame(() => {
            const elements = quotesSource.map((item, index) => {
              const { metadata, pageContent } = item;
              return (
                <ShellLink
                  title={metadata?.title}
                  aria-label={metadata?.title}
                  data-openreplay-label={`Link | ${metadata?.url || "#"}`}
                  id="lnk-quotes"
                  key={index}
                  target="_blank"
                  href={metadata?.url || "#"}
                  className="rounded-lg p-2 block hover:bg-background_white_overlay5"
                >
                  <div
                    className={cn(
                      "body_medium_m text-content_primary line-clamp-1",
                      {
                        "line-clamp-3": !pageContent,
                      },
                    )}
                  >
                    {metadata?.title}
                  </div>
                  <div
                    className={cn(
                      "body_s text-content_tertiary mt-1 line-clamp-2",
                      {
                        "line-clamp-3": !metadata?.title,
                      },
                    )}
                  >
                    {pageContent}
                  </div>
                  <SourceFooter
                    metaData={metadata}
                    logoSize={16}
                    containerClassName="mt-2"
                  />
                </ShellLink>
              );
            });
            resolve(elements);
          });
        },
      );

      // 计算位置
      const position = {
        top: rect.top + (window.scrollY || 0),
        left:
          window.innerWidth - rect.right < rect.left
            ? rect.left + (window.scrollX || 0) - 400
            : rect.left + (window.scrollX || 0) + 17,
      };

      // 确保内容已准备好并且有效
      if (contentElements.length === 0) {
        return;
      }

      // 创建隐藏的Portal，先不显示
      const newPortal = createPortal(
        <div
          className="fixed z-[9999] opacity-0"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            transition: "opacity 0.15s ease-in",
          }}
          id="quotes-portal-container"
        >
          <div className="bg-background_solid_tertiary rounded-lg border border-background_white_overlay10 overflow-auto w-[400px] max-h-[524px] p-2">
            {contentElements}
          </div>
        </div>,
        document.body,
      );

      // 设置Portal
      setPortal(newPortal);

      // 使用另一个requestAnimationFrame确保Portal已经挂载到DOM
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // 标记内容准备完成，可以显示
          setIsContentReady(true);

          // 找到Portal容器并移除隐藏样式
          const portalContainer = document.getElementById(
            "quotes-portal-container",
          );
          if (portalContainer) {
            portalContainer.classList.remove("opacity-0");
          }
        });
      });
    };

    prepareContent();

    // 清理函数
    return () => {
      setIsContentReady(false);
    };
  }, [canRenderPopup, isHovering, quotesSource, rect]);

  return (
    <span
      className={cn(
        "hidden md:inline-flex cursor-pointer relative px-1 quotes-container",
        {
          group: hasSourceContent,
          "has-popup": canRenderPopup,
        },
      )}
      ref={ref}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      data-has-content={canRenderPopup}
      data-content-ready={isContentReady}
    >
      <span
        className={cn(
          "rounded-full inline-flex items-center justify-center transition-colors duration-200 rounded-full inline-flex bg-background_white_overlay5 w-4 h-4 items-center justify-center",
          {
            "hover:bg-content_primary": canRenderPopup,
            "bg-content_primary": isHovering && canRenderPopup,
          },
        )}
      >
        <Icon
          name="globe"
          size={12}
          className={cn("text-content_tertiary", {
            "hover:text-content_inverse_primary": canRenderPopup,
            "text-content_inverse_primary": isHovering && canRenderPopup,
          })}
        />
      </span>
      {portal}
    </span>
  );
};

export default Quotes;
